import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Button,
  TextField,
  Avatar,
  List,
  ListItem,
  IconButton,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import LoginIcon from '@mui/icons-material/Login';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useAuth } from '../contexts/AuthContext';
import LoginDialog from '../components/LoginDialog';
import axios from 'axios';
import SEO from '../components/SEO';

const MAX_CONTENT_LENGTH = 2000;

const formatTimestamp = (timestamp) =>
  new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

export default function ForumTopicPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [topic, setTopic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    axios.get(`/api/forum/topics/${topicId}`)
      .then(res => {
        setTopic(res.data.topic);
        setMessages(res.data.messages);
        setError(null);
      })
      .catch(() => setError('Failed to load topic. Please try again.'))
      .finally(() => setLoading(false));
  }, [topicId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!currentUser) { setLoginDialogOpen(true); return; }
    if (!newMessage.trim() || sendingMessage) return;

    try {
      setSendingMessage(true);
      const response = await axios.post(`/api/forum/topics/${topicId}/messages`, {
        content: newMessage,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userAvatar: currentUser.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser.uid}`
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const charsLeft = MAX_CONTENT_LENGTH - newMessage.length;
  const isOverLimit = charsLeft < 0;

  return (
    <Box
      component="main"
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: '1100px',
        mx: 'auto',
        backgroundColor: '#221FC7',
        borderRadius: 2,
        color: 'white',
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SEO
        title={topic?.title || 'Forum Topic'}
        description={topic?.description || `Join the discussion: ${topic?.title || 'Forum topic'}.`}
        keywords={`pokemon forum, ${topic?.title || 'discussion'}, vgc discussion, pokemon strategy, community`}
      />

      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton
          onClick={() => navigate('/forum')}
          aria-label="Back to forum"
          sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          {loading ? (
            <Skeleton variant="text" width={200} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          ) : (
            <>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                component="h1"
                sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}
              >
                {topic?.title || 'Forum Topic'}
              </Typography>
              {topic?.description && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mt: 0.25 }}>
                  {topic.description}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.12)' }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          mb: 2,
          overflow: 'auto',
          bgcolor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2 },
          maxHeight: '60vh'
        }}
        role="log"
        aria-label="Forum messages"
        aria-live="polite"
      >
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 0.5 }} />
                  <Skeleton variant="rectangular" height={52} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 1.5 }} />
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              No messages yet. Be the first to join the discussion!
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: '100%', p: 0 }}>
            {messages.map((message) => {
              const isMine = message.userId === currentUser?.uid;
              return (
                <ListItem
                  key={message.id}
                  disableGutters
                  disablePadding
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: isMine ? 'flex-end' : 'flex-start',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMine ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      gap: 1,
                      maxWidth: { xs: '88%', sm: '75%', md: '65%' }
                    }}
                  >
                    <Avatar
                      src={message.userAvatar}
                      alt={`${message.userName}'s avatar`}
                      sx={{
                        width: { xs: 30, sm: 34 },
                        height: { xs: 30, sm: 34 },
                        flexShrink: 0,
                        display: { xs: isMine ? 'none' : 'flex', sm: 'flex' }
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMine ? 'row-reverse' : 'row',
                          alignItems: 'baseline',
                          gap: 1,
                          mb: 0.4,
                          px: 0.5
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: isMine ? '#24CC9F' : 'rgba(255,255,255,0.8)', fontSize: '0.78rem' }}
                        >
                          {message.userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}>
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: isMine ? '#24CC9F' : 'rgba(255, 255, 255, 0.12)',
                          color: isMine ? '#0a2e22' : '#ffffff',
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 0.8, sm: 1 },
                          borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          wordBreak: 'break-word',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' }, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      {/* Input area */}
      {currentUser ? (
        <Paper
          component="form"
          onSubmit={handleSendMessage}
          elevation={0}
          sx={{
            p: { xs: 1, sm: 1.5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <TextField
              fullWidth
              id="message-input"
              placeholder="Write your message... (Ctrl+Enter to send)"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="standard"
              multiline
              maxRows={5}
              inputProps={{ 'aria-label': 'Message content', maxLength: MAX_CONTENT_LENGTH + 100 }}
              InputProps={{
                disableUnderline: true,
                sx: {
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  '& textarea::placeholder': { color: 'rgba(255, 255, 255, 0.45)', opacity: 1 }
                }
              }}
            />
            <Button
              type="submit"
              disabled={sendingMessage || !newMessage.trim() || isOverLimit}
              aria-label="Send message"
              sx={{
                minWidth: 'unset',
                width: { xs: 38, sm: 42 },
                height: { xs: 38, sm: 42 },
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: '#24CC9F',
                color: '#0a2e22',
                '&:hover': { bgcolor: '#1bab85' },
                '&.Mui-disabled': { bgcolor: 'rgba(36, 204, 159, 0.3)', color: 'rgba(0,0,0,0.4)' }
              }}
            >
              <SendIcon fontSize={isMobile ? 'small' : 'medium'} />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: isOverLimit ? '#ff6b6b' : 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
            >
              {newMessage.length}/{MAX_CONTENT_LENGTH}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2,
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.75)', textAlign: { xs: 'center', sm: 'left' } }}
          >
            Sign in to participate in this discussion
          </Typography>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => setLoginDialogOpen(true)}
            aria-label="Sign in to participate"
            sx={{
              bgcolor: '#24CC9F',
              color: '#0a2e22',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1bab85' },
              minWidth: { xs: '100%', sm: 'auto' },
              flexShrink: 0
            }}
          >
            Sign In
          </Button>
        </Paper>
      )}

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        isSignUp={false}
      />
    </Box>
  );
}
